// internal dependencies
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";
import {
    PaymentGroupDataDef,
    PaymentMethodDef,
    IB2CPaymentGroup,
    B2CPaymentMethodData,
    SplitPayData,
    B2CHalfCardConfig,
} from "@swiggy-private/payment-ui";
import { None, Option, Some } from "@swiggy-private/js-fp";
// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// helpers
import { getPaymentMethodFromGroup, isPreferredGroup } from "./PaymentHelper";

export function resolveShowExpenseCodeHalfCard(
    methodFlag?: boolean,
    isWidgetEnabled?: boolean,
    widgetFlag?: boolean,
): boolean {
    return methodFlag ?? ((isWidgetEnabled ?? false) && (widgetFlag ?? false));
}

export function resolveB2CHalfCardConfig(
    methodConfig?: B2CHalfCardConfig | null,
    isWidgetEnabled?: boolean,
    widgetConfig?: B2CHalfCardConfig,
): B2CHalfCardConfig | null {
    return methodConfig ?? ((isWidgetEnabled ?? false) ? widgetConfig ?? null : null);
}

export function isBillToCompanyGroup(group: PaymentGroupDataDef): group is IB2CPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.BILL_TO_COMPANY;
}

export function isSingleClickGroup(group: PaymentGroupDataDef): boolean {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.SINGLE_CLICK;
}

export const isBillToCompanyPaymentMethod = (method: PaymentMethodDef): method is B2CPaymentMethodData =>
    method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.BILL_TO_COMPANY;

export function findBillToCompanyPaymentMethod(
    paymentResponse: GetPaymentMethodsResponse,
    paymentCode: string,
): Option<B2CPaymentMethodData | SplitPayData> {
    const paymentGroups = paymentResponse.paymentGroup;
    const splitpayList: GetPaymentMethodsResponse["splitpayList"] = paymentResponse.splitpayList;

    const b2cGroup = paymentGroups?.find(isBillToCompanyGroup);
    let billToCompanyPaymentMethod = b2cGroup ? getPaymentMethodFromGroup(b2cGroup, paymentCode) : None;

    if (billToCompanyPaymentMethod.isNone()) {
        // check if the method is in preferred group
        const preferredGroup = paymentGroups?.find(isPreferredGroup);
        billToCompanyPaymentMethod = preferredGroup ? getPaymentMethodFromGroup(preferredGroup, paymentCode) : None;
    }

    if (billToCompanyPaymentMethod.isNone()) {
        // check if the method is in single click group (present when b_turbo=true)
        const singleClickGroup = paymentGroups?.find(isSingleClickGroup);
        billToCompanyPaymentMethod = singleClickGroup ? getPaymentMethodFromGroup(singleClickGroup, paymentCode) : None;
    }

    if (billToCompanyPaymentMethod.isNone()) {
        // check if the method is in splitpayList
        const splitPayMatch = splitpayList?.find(
            (item: SplitPayData) =>
                item?.name?.toLowerCase() === paymentCode?.toLowerCase() &&
                item?.paymentCode?.toLowerCase() === paymentCode?.toLowerCase(),
        );

        billToCompanyPaymentMethod = splitPayMatch ? Some(splitPayMatch) : None;
    }

    return billToCompanyPaymentMethod.match({
        none: None,
        some: (method) => {
            // Case 1: Has groupName — likely from paymentGroup/preferredGroup/singleClickGroup
            if ("groupName" in method) {
                return isBillToCompanyPaymentMethod(method as PaymentMethodDef) ||
                    (method as PaymentMethodDef).groupName?.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.SINGLE_CLICK
                    ? Some(method)
                    : None;
            }

            // Case 2: From splitpayList — accept it as valid B2CPaymentMethodData directly
            return Some(method as SplitPayData);
        },
    });
}
